import React, { useState, useEffect } from 'react'
import { Typography, withStyles, Collapse, IconButton } from '@material-ui/core'
import { Icon } from '@iconify/react'
import styles from './styles'
import { CoreButton } from 'src/components/core'
import { useSelector, useDispatch } from 'react-redux'
import EditModal from './edit-modal'

import {
  messageBoxHideAction,
  messageBoxShowAction,
} from 'src/reducers/message-box/action'
import { ButtonActionType, ButtonData, ButtonType } from 'src/model/button'
import MessageBoxContent, { MessageBoxType } from 'src/model/message-box'
import { PatchContent, PatchData } from 'src/model/patch'
import { patchCourseCollectionAction } from 'src/reducers/collection/action'
import { useSnackbar } from 'notistack'
import { portalPageUpdateService } from 'src/api/collection'
import { slugForRename } from 'src/utils/portal-page-slug'
import {
  getPortalPageEditing,
  setPortalPageEditing,
  showHeaderTitle,
} from 'src/utils/portal-page-editing'

const CollectionTitleEdit = ({ classes }) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const { collection } = useSelector(state => state.collection)
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  // A shop landing page (Catalog, About…) edits its own name, not the showcase's.
  const [portalPage, setPortalPage] = useState(getPortalPageEditing)
  const title = portalPage ? portalPage.name : collection.name
  // The portal's own page (showcase) is the portal, not a learning path.
  const titleLabel = portalPage
    ? 'Page title'
    : collection?.type === 1
      ? 'Portal title'
      : 'Learning path title'

  /** Renames the open shop landing page; its URL follows the name as in Navigation (slugForRename). */
  const renamePortalPage = name =>
    slugForRename(
      { type: portalPage.type, id: portalPage.pageId, slug: portalPage.slug },
      name,
    )
      .then(slug => portalPageUpdateService(portalPage.pageId, { name, slug }))
      .then(response => {
        const saved = response?.data?.pages?.find(
          page => page.id === portalPage.pageId,
        )
        const next = {
          ...portalPage,
          name: saved?.name || name,
          slug: saved?.slug ?? portalPage.slug,
          path: saved?.path || portalPage.path,
        }
        setPortalPage(next)
        setPortalPageEditing(next)
        showHeaderTitle(next.name)
        enqueueSnackbar('Saved', { variant: 'success' })
        dispatch(messageBoxHideAction('message-box-1'))
      })
      .catch(error =>
        enqueueSnackbar(
          error?.response?.data?.operationMessage ||
            'Could not rename the page.',
          { variant: 'error' },
        ),
      )

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded)
  }

  const editModal = () => {
    // Temporary state to store current modal values
    let currentModalData = {
      title: title || '',
    }

    dispatch(
      messageBoxShowAction(
        MessageBoxContent({
          type: MessageBoxType.WARNING,
          buttons: [
            new ButtonData(
              ButtonType.NORMAL,
              'Cancel',
              ButtonActionType.NO,
              () => {
                dispatch(messageBoxHideAction('message-box-1'))
              },
            ),
            new ButtonData(
              ButtonType.ACTION,
              'Save',
              ButtonActionType.YES,
              () => {
                // Get the latest title value from currentModalData
                const newTitle = currentModalData.title?.trim()

                if (portalPage) {
                  if (newTitle && newTitle !== portalPage.name) {
                    renamePortalPage(newTitle)
                  } else {
                    dispatch(messageBoxHideAction('message-box-1'))
                  }
                  return
                }

                if (collection?.name !== newTitle && newTitle) {
                  const patchSource = new PatchData(null, collection.id)
                  patchSource.addContent(
                    new PatchContent(
                      newTitle,
                      'collectionName',
                      PatchData.OPERATION_REPLACE,
                    ),
                  )
                  dispatch(
                    patchCourseCollectionAction(patchSource, () => {
                      // Close modal after save completes
                      dispatch(messageBoxHideAction('message-box-1'))
                    }),
                  )
                }
              },
            ),
          ],
          title: 'Edit title',
          component: (
            <EditModal
              title={title || ''}
              label={titleLabel}
              onSave={settings => {
                currentModalData = { ...settings }
              }}
            />
          ),
          maxWidth: '680px',
        }),
      ),
    )
  }

  return (
    <div className={classes.container} onClick={editModal}>
      <div>
        <Typography className={classes.header}>
          {titleLabel}
        </Typography>
      </div>

      <div className={classes.content}>
        <div className={classes.title}>{title}</div>
        <CoreButton color="secondary" variant="outlined" size="small">
          Edit title
        </CoreButton>
      </div>
    </div>
  )
}

export default withStyles(styles)(CollectionTitleEdit)
